import { CheckCircleIcon } from "@chakra-ui/icons";
import {
  Box,
  Checkbox,
  Grid,
  HStack,
  Input,
  Select as ChakraSelect,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import { allLanguages } from "../../../../../shared/consts";
import SharedSelect from "../../../../../shared/components/Select";
import { phoneFormatter } from "../../../../../shared/utils/phone-formatter";
import { formatCustomFieldValue } from "../../../../custom-field/custom-field.utils";
import CaregiverAddressPicker from "../../../../caregiver/components/CaregiverAddressPicker";
import CaregiverSendEmailButton from "../../../../caregiver/components/CaregiverSendEmailButton";
import RnPerformanceBlocksTable from "../../../../caregiver/components/RnPerformanceBlocks/RnPerformanceBlocksTable";
import EntityModalButton from "../../../../entityProfileModal/components/EntityModalButton";
import EntityProfileEditModeButton from "../../../../entityProfileModal/components/EntityProfileEditModeButton";
import EntityProfileInlineEditableRow from "../../../../entityProfileModal/components/EntityProfileInlineEditableRow";
import {
  EntityProfileSummaryActionRow as SummaryActionRow,
  EntityProfileSummaryBlock as SummaryBlock,
  EntityProfileSummaryRow as SummaryRow,
} from "../../../../entityProfileModal/components/EntityProfileSummary";
import {
  entityProfileModalBorders,
  entityProfileModalColors,
} from "../../../../entityProfileModal/entityProfileModal.theme";
import { CaregiverProfileCaregiver } from "../../../caregiverProfileModal.types";
import {
  formatCaregiverCertifications,
  formatCaregiverProfileValue,
} from "../../../caregiverProfileModal.utils";
import type { CaregiverProfileBridgeActions } from "../../../caregiverProfileModal.actions";
import {
  CaregiverAboutField,
  useCaregiverProfileAboutController,
} from "./CaregiverProfileAboutSection.hooks";

type Props = {
  actions: CaregiverProfileBridgeActions;
  caregiver: CaregiverProfileCaregiver;
};

type CaregiverAboutEditableRowProps = {
  activeField: CaregiverAboutField | null;
  children: React.ReactNode;
  emptyPlaceholder: boolean;
  field: CaregiverAboutField;
  isEditable: boolean;
  label: string;
  value: React.ReactNode;
  onActivate: (field: CaregiverAboutField) => void;
  onCancel: () => void;
  onCommit: () => void;
};

function formatList(values: readonly unknown[]) {
  return values.length > 0 ? values.map((value) => String(value)).join(", ") : "";
}

function formatSsn(value: string | null) {
  if (!value) {
    return "";
  }

  const trimmedValue = value.toString().trim().replace(/^\+/, "");

  if (/[^0-9]/.test(trimmedValue)) {
    return value;
  }

  const firstPart = trimmedValue.slice(0, 3);
  let rest = trimmedValue.slice(3);

  if (rest.length > 2) {
    rest = `${rest.slice(0, 2)}-${rest.slice(2, 6)}`;
  }

  return rest ? `${firstPart}-${rest}`.trim() : firstPart;
}

function isRnCertification(value: unknown) {
  const normalizedValue = String(value ?? "")
    .trim()
    .toLowerCase();

  return normalizedValue === "rn" || normalizedValue === "registered nurse";
}

function formatBranches(
  branchNames: CaregiverProfileCaregiver["branchNames"],
  branchIds: CaregiverProfileCaregiver["branchIds"],
  branchOptions: Array<{ value: CaregiverProfileCaregiver["branchIds"][number]; label: string }>
) {
  if (branchNames !== undefined && branchNames.length > 0) {
    return branchNames.join(", ");
  }

  if (branchIds.length === 0) {
    return "";
  }

  return branchIds
    .map(
      (branchId) =>
        branchOptions.find((option) => option.value === branchId)?.label ?? `Branch #${branchId}`
    )
    .join(", ");
}

function formatOffices(
  officeNames: CaregiverProfileCaregiver["officeNames"],
  officeIds: CaregiverProfileCaregiver["officeIds"],
  officeOptions: Array<{ value: CaregiverProfileCaregiver["officeIds"][number]; label: string }>
) {
  if (officeNames !== undefined && officeNames.length > 0) {
    return officeNames.join(", ");
  }

  if (officeIds.length === 0) {
    return "";
  }

  return officeIds
    .map(
      (officeId) =>
        officeOptions.find((option) => option.value === officeId)?.label ?? `Office #${officeId}`
    )
    .join(", ");
}

function formatPhoneNumber(
  phoneNumber: CaregiverProfileCaregiver["phoneNumbers"][number],
  fallbackType?: string
) {
  const type = phoneNumber.type || fallbackType;
  const formattedPhoneNumber = phoneFormatter.formatNationalIfValid(phoneNumber.phoneNumber);

  return type ? `${formattedPhoneNumber} (${type})` : formattedPhoneNumber;
}

function formatSmsPhoneNumber(value: string) {
  const trimmedValue = value.trim();

  if (trimmedValue.startsWith("+")) {
    return trimmedValue;
  }

  const digits = trimmedValue.replace(/\D/g, "");

  if (digits.length === 11 && digits.startsWith("1")) {
    return `+${digits}`;
  }

  if (digits.length === 10) {
    return `+1${digits}`;
  }

  return `+1${trimmedValue}`;
}

function toDateInputValue(value: LocalDate | null) {
  return value === null ? "" : value.toString();
}

function CaregiverAboutTextInputRow(
  props: Omit<CaregiverAboutEditableRowProps, "children"> & {
    inputValue: string;
    onChange: (value: string) => void;
  }
) {
  return (
    <EntityProfileInlineEditableRow
      activeField={props.activeField}
      emptyPlaceholder={props.emptyPlaceholder}
      field={props.field}
      isEditable={props.isEditable}
      label={props.label}
      value={props.value}
      onActivate={props.onActivate}
      onCancel={props.onCancel}
      onCommit={props.onCommit}
    >
      <Input
        size="sm"
        value={props.inputValue}
        onChange={(event) => props.onChange(event.target.value)}
      />
    </EntityProfileInlineEditableRow>
  );
}

export default function CaregiverProfileAboutSection(props: Props) {
  const { caregiver } = props;
  const caregiverWithAgencyAssoc = caregiver as CaregiverProfileCaregiver & {
    authenticareWorkerId?: string | null;
    employmentType?: string | null;
    isEmailVerified?: boolean | null;
    isTestUser?: boolean;
  };
  const controller = useCaregiverProfileAboutController(caregiver, props.actions);
  const addressDisclosure = useDisclosure();
  const languageOptions = allLanguages.map((language) => ({ label: language, value: language }));
  const certificationOptions = controller.certifications.map((certification) => ({
    label: certification.certification,
    value: certification.certification,
  }));
  const officeOptions = controller.offices.map((office) => ({
    label: office.name,
    value: office.id,
  }));
  const branchOptions = controller.branches.map((branch) => ({
    label: branch.name,
    value: branch.id,
  }));
  const officeBranchOptions = controller.officeBranches.map((branch) => ({
    label: branch.name,
    value: branch.id,
  }));
  const customFields = Object.entries(caregiver.customFields ?? {});
  const isRnCaregiver =
    caregiver.certifications.some(isRnCertification) ||
    (caregiver.certificationNames ?? []).some(isRnCertification);
  const canShowClockIn = controller.agencyId === 7 || controller.agencyId === 373;
  const editableRowProps = (
    field: CaregiverAboutField,
    options: { isEditable?: boolean } = {}
  ) => ({
    activeField: controller.activeField,
    emptyPlaceholder: controller.isEditing,
    field,
    isEditable:
      options.isEditable ?? (controller.canEdit && controller.isEditing && !controller.isSaving),
    onActivate: controller.setActiveField,
    onCancel: controller.cancelField,
    onCommit: () => controller.commitField(field),
  });

  return (
    <VStack alignItems="stretch" spacing={4}>
      <CaregiverAddressPicker caregiverId={caregiver.id} disclosure={addressDisclosure} />
      {controller.canEdit ? (
        <EntityProfileEditModeButton
          isDisabled={controller.isSaving}
          isEditing={controller.isEditing}
          label="Edit"
          onClick={() => {
            controller.setActiveField(null);
            controller.setIsEditing(!controller.isEditing);
          }}
        />
      ) : null}

      <Grid gap={8} templateColumns={{ base: "1fr", xl: "repeat(2, minmax(0, 1fr))" }}>
        <VStack alignItems="stretch" spacing={8}>
          <SummaryBlock title="Status">
            <SummaryRow
              isClickable={controller.isEditing && controller.canChangeStatus}
              label="Status"
              value={formatCaregiverProfileValue(caregiver.status)}
              onClick={
                controller.isEditing && controller.canChangeStatus
                  ? controller.openChangeStatus
                  : undefined
              }
            />
            {controller.isEditing && !controller.canChangeStatus ? (
              <Box borderBottom={entityProfileModalBorders.default} py={2}>
                <Text color={entityProfileModalColors.muted} fontSize="13px">
                  You do not have permission to edit caregiver status.
                </Text>
              </Box>
            ) : null}
          </SummaryBlock>
          {isRnCaregiver ? <RnPerformanceBlocksTable caregiverId={caregiver.id} /> : null}

          <SummaryBlock title="Personal Details">
            <SummaryRow label="ID" value={formatCaregiverProfileValue(caregiver.displayId)} />
            {controller.agencyId === 6205 ? (
              <SummaryRow
                label="External ID"
                value={formatCaregiverProfileValue(caregiver.externalId)}
              />
            ) : null}
            <CaregiverAboutTextInputRow
              inputValue={controller.draft.ssn ?? ""}
              label="Social Security Number (SSN)"
              value={formatSsn(caregiver.ssn)}
              {...editableRowProps("ssn", {
                isEditable:
                  controller.canEdit &&
                  controller.canEditSsn &&
                  controller.isEditing &&
                  !controller.isSaving,
              })}
              onChange={(value) => controller.updateDraft("ssn", value)}
            />
            <CaregiverAboutTextInputRow
              inputValue={controller.draft.firstName}
              label="First Name"
              value={formatCaregiverProfileValue(caregiver.firstName)}
              {...editableRowProps("firstName")}
              onChange={(value) => controller.updateDraft("firstName", value)}
            />
            <CaregiverAboutTextInputRow
              inputValue={controller.draft.middleName ?? ""}
              label="Middle Name"
              value={formatCaregiverProfileValue(caregiver.middleName)}
              {...editableRowProps("middleName")}
              onChange={(value) => controller.updateDraft("middleName", value)}
            />
            <CaregiverAboutTextInputRow
              inputValue={controller.draft.lastName}
              label="Last Name"
              value={formatCaregiverProfileValue(caregiver.lastName)}
              {...editableRowProps("lastName")}
              onChange={(value) => controller.updateDraft("lastName", value)}
            />
            <EntityProfileInlineEditableRow
              label="Gender"
              value={formatCaregiverProfileValue(caregiver.gender)}
              {...editableRowProps("gender")}
            >
              <ChakraSelect
                size="sm"
                value={controller.draft.gender}
                onChange={(event) => controller.updateDraft("gender", event.target.value)}
              >
                <option value="F">F</option>
                <option value="M">M</option>
              </ChakraSelect>
            </EntityProfileInlineEditableRow>
            <EntityProfileInlineEditableRow
              label="Birth Date"
              value={formatCaregiverProfileValue(caregiver.birthDateDate ?? caregiver.birthDate)}
              {...editableRowProps("birthDateDate")}
            >
              <Input
                size="sm"
                type="date"
                value={toDateInputValue(controller.draft.birthDateDate)}
                onChange={(event) =>
                  controller.updateDraft(
                    "birthDateDate",
                    event.target.value === "" ? null : LocalDate.parse(event.target.value)
                  )
                }
              />
            </EntityProfileInlineEditableRow>
            <SummaryRow
              actions={
                controller.isEditing ? (
                  <EntityModalButton kind="white" size="xs" onClick={addressDisclosure.onOpen}>
                    Customize
                  </EntityModalButton>
                ) : null
              }
              label="Address"
              value={
                caregiver.isCustomAddress ? `${caregiver.address} (custom)` : caregiver.address
              }
            />
            <EntityProfileInlineEditableRow
              label="Address 2"
              value={formatCaregiverProfileValue(caregiver.address2)}
              {...editableRowProps("address2")}
            >
              <Input
                size="sm"
                value={controller.draft.address2 ?? ""}
                onChange={(event) => controller.updateDraft("address2", event.target.value)}
              />
            </EntityProfileInlineEditableRow>
            <SummaryRow
              label="County"
              value={formatCaregiverProfileValue(caregiver.addressCounty)}
            />
            <EntityProfileInlineEditableRow
              cancelOnBlur={false}
              label="Main Language"
              value={formatCaregiverProfileValue(caregiver.mainLanguage)}
              {...editableRowProps("mainLanguage")}
            >
              <SharedSelect
                label="Main language"
                multiple={false}
                options={languageOptions}
                value={controller.draft.mainLanguage}
                onChange={(value) => controller.updateDraft("mainLanguage", value ?? "")}
              />
            </EntityProfileInlineEditableRow>
            <EntityProfileInlineEditableRow
              cancelOnBlur={false}
              label="Languages"
              value={formatList(caregiver.languages)}
              {...editableRowProps("languages")}
            >
              <SharedSelect
                label="Languages"
                multiple={true}
                options={languageOptions}
                value={controller.draft.languages}
                onChange={(value) => controller.updateDraft("languages", value ?? [])}
              />
            </EntityProfileInlineEditableRow>
            {controller.flags !== null ? (
              <SummaryRow
                label="Flags"
                value={
                  controller.isEditing ? (
                    <VStack alignItems="flex-start" spacing={1}>
                      {Object.entries(controller.flags).map(([flag, active]) => (
                        <Checkbox
                          key={flag}
                          isChecked={active === true}
                          isDisabled={controller.isUpdatingFlag}
                          size="sm"
                          onChange={(event) =>
                            controller.updateFlag(
                              flag as Parameters<typeof controller.updateFlag>[0],
                              event.target.checked
                            )
                          }
                        >
                          {flag}
                        </Checkbox>
                      ))}
                    </VStack>
                  ) : (
                    formatList(
                      Object.entries(controller.flags)
                        .filter(([, active]) => active)
                        .map(([flag]) => flag)
                    )
                  )
                }
              />
            ) : null}
          </SummaryBlock>
        </VStack>

        <VStack alignItems="stretch" spacing={8}>
          <SummaryBlock title="Contact Information">
            <EntityProfileInlineEditableRow
              label="Email"
              value={
                <HStack spacing={2}>
                  {caregiverWithAgencyAssoc.isEmailVerified === true ? (
                    <CheckCircleIcon boxSize={3.5} color="green.500" />
                  ) : null}
                  <Text as="span">{formatCaregiverProfileValue(caregiver.email)}</Text>
                </HStack>
              }
              {...editableRowProps("email")}
            >
              <Input
                size="sm"
                value={controller.draft.email ?? ""}
                onChange={(event) => controller.updateDraft("email", event.target.value)}
              />
            </EntityProfileInlineEditableRow>
            {controller.isOnboardingAgency && caregiver.email ? (
              <SummaryActionRow>
                <CaregiverSendEmailButton
                  buttonProps={{ size: "xs", variant: "outline" }}
                  caregiverId={caregiver.id}
                  text="Send email"
                />
              </SummaryActionRow>
            ) : null}
            {caregiver.phoneNumbers.length === 0 ? (
              <Box borderBottom={entityProfileModalBorders.default} py={2}>
                <Text color={entityProfileModalColors.text} fontSize="14px">
                  Caregiver has no phone numbers.
                </Text>
              </Box>
            ) : (
              caregiver.phoneNumbers.map((phoneNumber, index) => (
                <SummaryRow
                  key={`${phoneNumber.phoneNumber}-${index}`}
                  actions={
                    controller.isEditing ? (
                      <EntityModalButton
                        aria-label={`Delete phone number ${formatPhoneNumber(phoneNumber)}`}
                        disabled={controller.isDeletingPhoneNumber}
                        iconClassName="fa fa-trash"
                        kind="red"
                        size="xs"
                        title="Delete phone number"
                        variant="outline"
                        onClick={() => controller.deletePhoneNumber(phoneNumber)}
                      />
                    ) : controller.isOnboardingAgency ? (
                      <HStack spacing={1}>
                        <EntityModalButton
                          aria-label={`Call ${formatPhoneNumber(phoneNumber)}`}
                          iconClassName="fa fa-phone"
                          kind="white"
                          size="xs"
                          title="Call"
                          onClick={() =>
                            props.actions.openDialer({
                              predefinedPhoneNumber: phoneNumber.phoneNumber,
                            })
                          }
                        >
                          Call
                        </EntityModalButton>
                        {phoneNumber.type === "mobile" ? (
                          <EntityModalButton
                            kind="blue"
                            size="xs"
                            variant="outline"
                            onClick={() =>
                              props.actions.openSmsSender({
                                caregiverId: caregiver.id,
                                phoneNumber: formatSmsPhoneNumber(phoneNumber.phoneNumber),
                                type: "Caregiver",
                              })
                            }
                          >
                            SMS
                          </EntityModalButton>
                        ) : null}
                      </HStack>
                    ) : null
                  }
                  label="Phone"
                  value={formatPhoneNumber(phoneNumber)}
                />
              ))
            )}
            <EntityProfileInlineEditableRow
              label="Home phone"
              value={formatCaregiverProfileValue(caregiver.homePhone)}
              {...editableRowProps("homePhone")}
            >
              <Input
                size="sm"
                value={controller.draft.homePhone ?? ""}
                onChange={(event) => controller.updateDraft("homePhone", event.target.value)}
              />
            </EntityProfileInlineEditableRow>
            {controller.isEditing ? (
              <SummaryActionRow>
                {controller.isAddingPhoneNumber ? (
                  <HStack spacing={2}>
                    <Input
                      maxW="220px"
                      placeholder="Enter Phone Number"
                      size="sm"
                      value={controller.phoneNumberDraft}
                      onChange={(event) => controller.setPhoneNumberDraft(event.target.value)}
                    />
                    <EntityModalButton
                      disabled={controller.isSaving || controller.phoneNumberDraft.trim() === ""}
                      kind="blue"
                      size="sm"
                      onClick={controller.addPhoneNumber}
                    >
                      Save
                    </EntityModalButton>
                  </HStack>
                ) : (
                  <EntityModalButton
                    disabled={controller.isSaving}
                    iconClassName="fa fa-plus"
                    kind="blue"
                    size="sm"
                    variant="outline"
                    onClick={() => controller.setIsAddingPhoneNumber(true)}
                  >
                    Add Phone
                  </EntityModalButton>
                )}
              </SummaryActionRow>
            ) : null}
          </SummaryBlock>

          <SummaryBlock title="Professional Details">
            <EntityProfileInlineEditableRow
              cancelOnBlur={false}
              label="Certifications"
              value={formatCaregiverCertifications(
                caregiver.certifications,
                caregiver.certificationNames
              )}
              {...editableRowProps("certifications")}
            >
              <SharedSelect
                label="Certifications"
                multiple={true}
                options={certificationOptions}
                value={controller.draft.certifications}
                onChange={(value) => controller.updateDraft("certifications", value ?? [])}
              />
            </EntityProfileInlineEditableRow>
            <EntityProfileInlineEditableRow
              label="Registry number"
              value={formatCaregiverProfileValue(caregiver.stateRegistryNum)}
              {...editableRowProps("stateRegistryNum")}
            >
              <Input
                size="sm"
                type="number"
                value={controller.draft.stateRegistryNum ?? ""}
                onChange={(event) =>
                  controller.updateDraft(
                    "stateRegistryNum",
                    event.target.value === "" ? null : Number(event.target.value)
                  )
                }
              />
            </EntityProfileInlineEditableRow>
            {controller.showHcwIdFeature ? (
              <CaregiverAboutTextInputRow
                inputValue={controller.draft.hcwId ?? ""}
                label="HCW ID"
                value={formatCaregiverProfileValue(caregiver.hcwId)}
                {...editableRowProps("hcwId")}
                onChange={(value) => controller.updateDraft("hcwId", value)}
              />
            ) : null}
            <EntityProfileInlineEditableRow
              label="Employment Type"
              value={formatCaregiverProfileValue(caregiverWithAgencyAssoc.employmentType)}
              {...editableRowProps("employmentType")}
            >
              <ChakraSelect
                size="sm"
                value={controller.draft.employmentType ?? ""}
                onChange={(event) => controller.updateDraft("employmentType", event.target.value)}
              >
                <option value="">empty</option>
                <option value="Full Time">Full Time</option>
                <option value="Per Diem">Per Diem</option>
              </ChakraSelect>
            </EntityProfileInlineEditableRow>
            <EntityProfileInlineEditableRow
              label="Authenticare Worker ID"
              value={formatCaregiverProfileValue(caregiverWithAgencyAssoc.authenticareWorkerId)}
              {...editableRowProps("authenticareWorkerId")}
            >
              <Input
                size="sm"
                value={controller.draft.authenticareWorkerId ?? ""}
                onChange={(event) =>
                  controller.updateDraft("authenticareWorkerId", event.target.value)
                }
              />
            </EntityProfileInlineEditableRow>
            <EntityProfileInlineEditableRow
              cancelOnBlur={false}
              label="Offices"
              value={formatOffices(caregiver.officeNames, caregiver.officeIds, officeOptions)}
              {...editableRowProps("officeIds")}
            >
              <SharedSelect
                label="Offices"
                multiple={true}
                options={officeOptions}
                value={controller.draft.officeIds}
                onChange={(value) => controller.updateDraft("officeIds", value ?? [])}
              />
            </EntityProfileInlineEditableRow>
            {officeBranchOptions.length > 0 ? (
              <EntityProfileInlineEditableRow
                cancelOnBlur={false}
                label="Office Branches"
                value={formatList(
                  controller.officeBranches
                    .filter((branch) => (caregiver.officeBranchIds ?? []).includes(branch.id))
                    .map((branch) => branch.name)
                )}
                {...editableRowProps("officeBranchIds", {
                  isEditable:
                    controller.canEdit &&
                    controller.isEditing &&
                    !controller.isSaving &&
                    !controller.isLoadingOfficeBranches,
                })}
              >
                <SharedSelect
                  label="Office Branches"
                  multiple={true}
                  options={officeBranchOptions}
                  value={controller.draft.officeBranchIds}
                  onChange={(value) => controller.updateDraft("officeBranchIds", value ?? [])}
                />
              </EntityProfileInlineEditableRow>
            ) : null}
            <EntityProfileInlineEditableRow
              cancelOnBlur={false}
              label="Branches"
              value={formatBranches(caregiver.branchNames, caregiver.branchIds, branchOptions)}
              {...editableRowProps("branchIds", {
                isEditable:
                  controller.canEdit &&
                  controller.isEditing &&
                  !controller.isSaving &&
                  !controller.isLoadingBranches,
              })}
            >
              <SharedSelect
                label="Branches"
                multiple={true}
                options={branchOptions}
                value={controller.draft.branchIds}
                onChange={(value) => controller.updateDraft("branchIds", value ?? [])}
              />
            </EntityProfileInlineEditableRow>
            {controller.isOnboardingAgency ? (
              <EntityProfileInlineEditableRow
                label="Is Test User"
                value={formatCaregiverProfileValue(caregiverWithAgencyAssoc.isTestUser)}
                {...editableRowProps("isTestUser", {
                  isEditable:
                    controller.canEdit &&
                    controller.canEditTestUser &&
                    controller.isEditing &&
                    !controller.isSaving,
                })}
              >
                <Checkbox
                  isChecked={controller.draft.isTestUser}
                  onChange={(event) => controller.updateDraft("isTestUser", event.target.checked)}
                >
                  {controller.draft.isTestUser ? "Yes" : "No"}
                </Checkbox>
              </EntityProfileInlineEditableRow>
            ) : null}
            {customFields.map(([fieldName, value]) => (
              <SummaryRow key={fieldName} label={fieldName} value={formatCustomFieldValue(value)} />
            ))}
          </SummaryBlock>

          <SummaryBlock title="Services Information">
            {controller.isOnboardingAgency ? (
              <SummaryRow
                label="Is Medflyt CG"
                value={formatCaregiverProfileValue(caregiver.isFromLandingPage)}
              />
            ) : null}
            <EntityProfileInlineEditableRow
              label="Hire Date"
              value={formatCaregiverProfileValue(caregiver.hireDate)}
              {...editableRowProps("hireDate")}
            >
              <Input
                size="sm"
                type="date"
                value={toDateInputValue(controller.draft.hireDate)}
                onChange={(event) =>
                  controller.updateDraft(
                    "hireDate",
                    event.target.value === "" ? null : LocalDate.parse(event.target.value)
                  )
                }
              />
            </EntityProfileInlineEditableRow>
            <EntityProfileInlineEditableRow
              label="Re-hire Date"
              value={formatCaregiverProfileValue(caregiver.rehireDate)}
              {...editableRowProps("rehireDate")}
            >
              <Input
                size="sm"
                type="date"
                value={toDateInputValue(controller.draft.rehireDate)}
                onChange={(event) =>
                  controller.updateDraft(
                    "rehireDate",
                    event.target.value === "" ? null : LocalDate.parse(event.target.value)
                  )
                }
              />
            </EntityProfileInlineEditableRow>
            <EntityProfileInlineEditableRow
              label="Termination Date"
              value={formatCaregiverProfileValue(caregiver.terminationDate)}
              {...editableRowProps("terminationDate")}
            >
              <Input
                size="sm"
                type="date"
                value={toDateInputValue(controller.draft.terminationDate)}
                onChange={(event) =>
                  controller.updateDraft(
                    "terminationDate",
                    event.target.value === "" ? null : LocalDate.parse(event.target.value)
                  )
                }
              />
            </EntityProfileInlineEditableRow>
            <EntityProfileInlineEditableRow
              label="Application Start Date"
              value={formatCaregiverProfileValue(caregiver.applicationStartDate)}
              {...editableRowProps("applicationStartDate")}
            >
              <Input
                size="sm"
                type="date"
                value={toDateInputValue(controller.draft.applicationStartDate)}
                onChange={(event) =>
                  controller.updateDraft(
                    "applicationStartDate",
                    event.target.value === "" ? null : LocalDate.parse(event.target.value)
                  )
                }
              />
            </EntityProfileInlineEditableRow>
            <EntityProfileInlineEditableRow
              label="First Work Date"
              value={formatCaregiverProfileValue(caregiver.firstWorkDate)}
              {...editableRowProps("firstWorkDate")}
            >
              <Input
                size="sm"
                type="date"
                value={toDateInputValue(controller.draft.firstWorkDate)}
                onChange={(event) =>
                  controller.updateDraft(
                    "firstWorkDate",
                    event.target.value === "" ? null : LocalDate.parse(event.target.value)
                  )
                }
              />
            </EntityProfileInlineEditableRow>
            <EntityProfileInlineEditableRow
              label="Last Work Date"
              value={formatCaregiverProfileValue(caregiver.lastWorkDate)}
              {...editableRowProps("lastWorkDate")}
            >
              <Input
                size="sm"
                type="date"
                value={toDateInputValue(controller.draft.lastWorkDate)}
                onChange={(event) =>
                  controller.updateDraft(
                    "lastWorkDate",
                    event.target.value === "" ? null : LocalDate.parse(event.target.value)
                  )
                }
              />
            </EntityProfileInlineEditableRow>
            {canShowClockIn ? (
              <EntityProfileInlineEditableRow
                label="Enable to clock in/out"
                value={caregiver.isClockEnabled ? "ON" : "OFF"}
                {...editableRowProps("isClockEnabled")}
              >
                <Checkbox
                  isChecked={controller.draft.isClockEnabled}
                  onChange={(event) =>
                    controller.updateDraft("isClockEnabled", event.target.checked)
                  }
                >
                  {controller.draft.isClockEnabled ? "ON" : "OFF"}
                </Checkbox>
              </EntityProfileInlineEditableRow>
            ) : null}
            <SummaryRow
              label="App Status"
              value={
                caregiver.appInstalled
                  ? caregiver.online
                    ? "Installed, online"
                    : "Installed, offline"
                  : caregiver.appInstalledDate
                  ? "Uninstalled"
                  : "Not installed"
              }
            />
            <SummaryRow
              label="App Installed Date"
              value={formatCaregiverProfileValue(caregiver.appInstalledDate)}
            />
            <SummaryRow
              actions={
                <EntityModalButton
                  kind="blue"
                  size="sm"
                  variant="outline"
                  onClick={controller.openExclusionLists}
                >
                  Check
                </EntityModalButton>
              }
              label="Exclusion lists"
              value=""
            />
            <SummaryRow
              label="App Install Count"
              value={formatCaregiverProfileValue(caregiver.appInstalledAt.length)}
            />
            <SummaryRow
              label="App Uninstall Count"
              value={formatCaregiverProfileValue(caregiver.appUninstalledAt.length)}
            />
            <SummaryRow
              label="Installed User Agent"
              value={formatCaregiverProfileValue(caregiver.installedUserAgent)}
            />
            {controller.shouldShowUnblockLogin ? (
              <SummaryActionRow>
                <EntityModalButton
                  iconClassName="fa fa-unlock"
                  kind="blue"
                  size="sm"
                  onClick={controller.unblockLogin}
                >
                  Unblock Login
                </EntityModalButton>
              </SummaryActionRow>
            ) : null}
          </SummaryBlock>

          <SummaryBlock title="Activity">
            <SummaryRow
              label="Created At"
              value={formatCaregiverProfileValue(caregiver.createdAt)}
            />
            {controller.createdByName ? (
              <SummaryRow
                label="Created By"
                value={formatCaregiverProfileValue(controller.createdByName)}
              />
            ) : null}
          </SummaryBlock>
        </VStack>
      </Grid>
    </VStack>
  );
}
